import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";

import useAuth from "~/hooks/use-auth";
import { useCartWithPendingStatus } from "~/hooks/use-cart";
import Logger from "~/services/logger/logger";
import { EvaUserAccountType } from "~/shared/eva/eva-user-account-type.enum";

export default function GetActiveShoppingCart() {
  const { isAuthenticated, user } = useAuth();
  const { cart, isLoadingCart } = useCartWithPendingStatus();
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);
  const getActiveShoppingCart = hooks.useCallService({ service: Core.GetActiveShoppingCart });
  const getShoppingCart = hooks.useCallService({ service: Core.GetShoppingCart });
  const setCurrentOrder = useSetRecoilState(state.checkout.shoppingCartState.request);
  const clearedRef = useRef(!1);
  const cartStableRef = useRef(!1);

  useEffect(() => {
    /**
     * La query deve partire solo se non ho un carrello, oppure se quel carrello contiene un account di tipo standard.
     * Questo perché a quel punto, il carrello guest (con o senza account guest) viene riassegnato all'account loggato.
     */
    cartStableRef.current =
      !cart?.ShoppingCart?.ID || cart?.ShoppingCart?.Customer?.AccountType === EvaUserAccountType.Standard;
  }, [cart?.ShoppingCart?.ID, cart?.ShoppingCart?.Customer?.AccountType]);

  const activeCartQuery = useQuery({
    queryKey: ["activeCart", user?.EmailAddress],
    queryFn: async () => {
      return getActiveShoppingCart().then(async (response) => {
        let activeCartId: string | null | "CLEAR" = response?.OrderID ? `${response.OrderID}` : null;
        if (activeCartId) {
          const activeCart = await getShoppingCart({
            // @ts-expect-error fucking sdk
            OrderID: activeCartId,
          });
          // Workaround to exclude COD orders (and maybe even Ideal)
          if (activeCart?.ShoppingCart.IsPlaced) {
            return "CLEAR";
          }

          if (activeCartId === `${cart?.ShoppingCart.ID}`) {
            return null;
          }

          return activeCartId;
        } else {
          if (cart?.ShoppingCart.IsPlaced) {
            return "CLEAR";
          }
        }

        return null;
      });
    },
    enabled: !!evaEndpointUrl && isAuthenticated && cartStableRef.current,
  });

  useEffect(() => {
    if (isLoadingCart || !activeCartQuery.data) {
      return;
    }

    if (activeCartQuery.data === "CLEAR") {
      if (!clearedRef.current) {
        Logger.instance.debug("GetActiveShoppingCart: clearing cart", {
          active: activeCartQuery.data,
        });
        clearedRef.current = !0;
        setCurrentOrder({ OrderID: void 0 });
      }
      return;
    }
    clearedRef.current = !1; // Reset the clear flag when the cart is valid
    if (activeCartQuery.data !== `${cart?.ShoppingCart.ID}`) {
      Logger.instance.debug("GetActiveShoppingCart: updating cart due to mismatch", {
        active: activeCartQuery.data,
        current: cart?.ShoppingCart.ID,
      });
      // @ts-expect-error fucking sdk
      setCurrentOrder({ OrderID: activeCartQuery.data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCartQuery.data, isLoadingCart]);

  return null;
}
